import { useEffect, useState } from 'react';
import { Form, Table, Input, Select, Button, Space, Row, Col, Modal, Upload, message } from 'antd';
import { getFeedbackManagementList, sendEmail, getSignedUrl, uploadToS3 } from "../services/feedbackManagement.js";
import { UploadOutlined } from '@ant-design/icons';
import moment from "moment";
const FeedbackManagement = () => {
  const [form] = Form.useForm();
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [visible, setVisible] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [fileList, setFileList] = useState([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [userData, setUserData] = useState({});
  const columns = [
    {
      title: 'Username',
      dataIndex: 'userName',
      key: 'userName',
    },
    {
      title: 'Feedback Location',
      dataIndex: 'conversationId',
      key: 'conversationId',
      render: (conversationId) => (conversationId ? "Feedback on Conversation Page" : "	Feedback on Help Center"),
    },
    {
      title: '	Feedback Category',
      dataIndex: 'checkName',
      key: 'checkName',
    },
    {
      title: 'Conversation Name',
      key: 'conversationName',
      dataIndex: 'conversationName',
      render: (text, record) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.open(`/conversations/${record.conversationId}`, '_blank');
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: 'Feedback Content',
      key: 'feedbackContent',
      dataIndex: 'feedbackContent',
      render: (feedbackContent) => (
        <div style={{
          whiteSpace: 'normal',    
          wordWrap: 'break-word',  
          wordBreak: 'break-word'  
        }}>
          {feedbackContent}
        </div>
      ),
    },
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) => createdAt ? moment(createdAt).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: 'Status',
      key: 'isReply',
      dataIndex: 'isReply',
      render: (isReply) => (isReply == 1 ? "Replyed" : "Not replyed"),
    },
    {
      title: 'Action',
      key: 'operation',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => { handleNavigate(record) }}>
            Reply
          </Button>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchFeedManagements();
  }, [pagination.current, pagination.pageSize]);


  const fetchFeedManagements = async (params = filters, newPagination = pagination) => {
    setLoading(true);
    try {
      const response = await getFeedbackManagementList({
        ...params,
        pageNum: newPagination.current,
        pageSize: newPagination.pageSize
      });
      if (response?.code === 200) {
        const rows = response.rows.map((item, index) => ({
          key: (newPagination.current - 1) * newPagination.pageSize + index + 1,
          ...item,
        }));
        setData({ rows, total: response.total });
      }
    } catch (error) {
      console.error("Fail to fetch feedback list:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (values) => {
    setFilters(values);
    setPagination({ ...pagination, current: 1 });
    fetchFeedManagements(values, pagination);
  };

  const handleReset = () => {
    form.resetFields();
    const newPagination = { current: 1, pageSize: 10 };
    setFilters({});
    setPagination(newPagination);
    fetchFeedManagements({}, newPagination);
  };

  const handleTableChange = ((pagination, filters) => {

    setPagination({ current: pagination.current, pageSize: pagination.pageSize });
    fetchFeedManagements(filters, pagination);
  });

  const handleNavigate = (record) => {
    setTitle('Responses to questions about ' + record.checkName + ' that you gave back during the session.')
    setUserData(record)
    setVisible(true)
  };

  const handleCancel = () => {
    setVisible(false)
    setTitle('');          // 重置标题
    setContent('');        // 清空内容
    setFileList([]);
  }
  // 文件上传处理函数
  const handleUpload = ({ fileList: newFileList }) => {
    setFileList(newFileList.filter(file => {
      // 过滤过大文件（二次验证）
      return file.size && file.size / 1024 / 1024 < 20
    }));
  };

  const uploadFile = async file => {
    try {
      // 等待getSignedUrl完成
      const fileResponse = await getSignedUrl({
        userId: userData.userId,
        fileName: file.name,
        fileType: file.type
      });
      fileResponse.fileName = file.name;
      // 等待uploadToS3完成
      await uploadToS3(fileResponse.signedPutUrl, file.originFileObj);
      return fileResponse;
    } catch (e) {
      message.error('文件上传到S3失败,邮件发送失败');
      setButtonLoading(false)
      handleCancel()
      throw e;
    }
  };

  const handleOk = async () => {
    const signedGetUrls = [];
    setButtonLoading(true)
    // 创建FormData对象
    const formData = new FormData();
    formData.append('title', title);
    formData.append('content', content);
    formData.append('userId', userData.userId);
    formData.append('id', userData.id);
    // 并行上传所有文件
    const uploadResults = await Promise.all(
      fileList.map(file => uploadFile(file))
    );
    // 收集上传结果
    uploadResults.forEach(item => {
      signedGetUrls.push(item.externalRef + ";" + item.fileName);
    });
    signedGetUrls.forEach(url => formData.append('attachments', url));
    sendEmail(formData)
      .then(response => {
        if (response && response.code == 200) {
          message.success('Email sent successfully');
          handleSearch();
        } else {
          message.error('Email sent Failed');
        }
      })
      .catch(() => {
        message.error('Email sent Failed');
      })
      .finally(() => {
        setButtonLoading(false)
        handleCancel()
      });
  };

  return (
    <div>
      <Form
        form={form}
        onFinish={handleSearch}
        initialValues={{
          searchValue: '',
          feedbackType: '',
          isReply: '',
          conversationId: '',
          checkId: ''
        }}
      >
        <Row>
          <Col span={6} style={{ marginRight: '10px' }}>
            <Form.Item name="searchValue"
              style={{ maxWidth: 500 }}
            >
              <Input placeholder="Please enter content to search"></Input>
            </Form.Item>
          </Col>
          <Col flex="auto">
            <Row gutter={8} align="middle">
              <Col span={8}>
                <Form.Item name="conversationId" label="Feedback location">
                  <Select
                    style={{ width: '100%' }}
                    options={[
                      { value: '1', label: 'Feedback on Conversation Page' },
                      { value: '0', label: 'Feedback on Help Center' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="checkId" label="Feedback Category">
                  <Select
                    style={{ width: '100%' }}
                    options={[
                      { value: '1', label: 'Involved Organization' },
                      { value: '2', label: 'Actionable Insights' },
                      { value: '3', label: 'Possible Incident Type' },
                      { value: '4', label: 'Explanation' },
                      { value: '5', label: 'Advice' },
                      { value: '6', label: 'Tips' },
                      { value: '7', label: 'Others' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="isReply" label="isReply">
                  <Select
                    style={{ width: '100%' }}
                    options={[
                      { value: '1', label: 'Replyed' },
                      { value: '0', label: 'Not replyed' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col flex="none"  style={{ marginLeft: '10px' }}>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">Search</Button>
                <Button onClick={handleReset}>Reset</Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        columns={columns}
        dataSource={data.rows}
        loading={loading}
        pagination={{
          current: pagination.current,
          total: data.total,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `${total} in total`,
        }}
        onChange={handleTableChange}
      />

      <Modal
        title="Send Email"
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        width={1000}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleOk} loading={buttonLoading}       // 显示加载动画
            disabled={buttonLoading}>
            Send Email
          </Button>,
        ]}
      >
        <div style={{ marginBottom: '20px' }}>
          <label>Subject: </label>
          <Input
            placeholder="Please enter the email subject"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ width: '100%', marginTop: '10px' }}
          />
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block' }}>Email Content：</label>
          <Input.TextArea
            placeholder="Please enter the email content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            style={{ width: '100%', height: '100px', marginTop: '10px' }}
          />
        </div>

        <div style={{
          marginBottom: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center', // 垂直居中:ml-citation{ref="4" data="citationList"}
          width: '100%'         // 确保撑满父容器
        }}>
          <label style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',  // 标签左对齐
            paddingLeft: '12px' // 保持输入框对齐
          }}>Attachments:</label>
          <Upload
            multiple
            fileList={fileList}
            onChange={handleUpload}
            beforeUpload={(file) => {
              const isLt20M = file.size / 1024 / 1024 < 20;
              if (!isLt20M) {
                message.error('File size must not exceed 20MB');
              }
              return false; // 阻止自动上传
            }}
          >
            <div style={{
              border: '1px solid #d9d9d9',
              padding: '20px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: '#fafafa'
            }}>
              <UploadOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
              <p style={{ marginTop: '10px' }}>Click or drag files to upload</p>
              <p style={{ color: '#999' }}>Multiple file formats supported, maximum size: 20MB per file</p>
            </div>
          </Upload>
        </div>
      </Modal>
    </div >
  );
};

export default FeedbackManagement;
